import _ from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import {
    ADAPTER_TYPE_WIRELESS,
    AGENT_IS_WIRELESS_ENABLED_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_STATUS_RUNNING,
} from '../agents/AgentConstants';
import {
    AGENT_VOIP_CONFIGURATION_ADAPTER_TYPE_FIELD,
    AGENT_VOIP_CONFIGURATION_REGISTRATION_ERROR,
    AGENT_VOIP_CONFIGURATION_REGISTRATION_SIGNALING_COLLECTION_FIELD,
    AGENT_VOIP_CONFIGURATION_REGISTRATION_STATUS_FIELD,
    AGENT_VOIP_CONFIGURATION_REGISTRATION_UPDATED_AT_FIELD,
    AGENT_VOIP_REGISTRATION_DETAIL_HAS_INFO,
    AGENT_VOIP_REGISTRATION_DETAIL_HAS_REGISTRATION_STATUS,
    AGENT_VOIP_REGISTRATION_DETAIL_IS_REGISTERED,
    AGENT_VOIP_REGISTRATION_DETAIL_LAST_REGISTERED,
    AGENT_VOIP_REGISTRATION_DETAIL_NO_REGISTRATION_MESSAGE,
    AGENT_VOIP_REGISTRATION_DETAIL_REGISTRATION_ERROR_OBJECT,
    AGENT_VOIP_REGISTRATION_DETAIL_REGISTRATION_STATUS,
    VOIP_REGISTRATION_STATUS_DIRECT_CALLING,
    VOIP_REGISTRATION_STATUS_ERROR,
    VOIP_REGISTRATION_STATUS_PENDING,
    VOIP_REGISTRATION_STATUS_REGISTERED,
} from './AgentVoIPConstants';
import { dateTimeFormatter } from '../../../utilities';

const voipWirelessNotAvailable = i18n._(
    t`Registration not attempted. Wireless interface is currently disabled or running Wi-Fi Connection Tests.`
);

export function buildVoIPRegistration(response) {
    const agentStatus = _.get(response, AGENT_STATUS_FIELD);

    // MDL First see if we have a registration status.  We can't just check to see if we are setup
    //  for VoIP, we need to have tried to register.
    const registrationStatus = _.get(response, AGENT_VOIP_CONFIGURATION_REGISTRATION_STATUS_FIELD);
    const hasRegistrationInfo = !_.isEmpty(registrationStatus);
    _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_HAS_INFO, hasRegistrationInfo);

    if (!hasRegistrationInfo) {
        return;
    }

    // Handle case nPoint is using wifi interface but its disable or running wifi connect test
    if (
        !response[AGENT_IS_WIRELESS_ENABLED_FIELD] &&
        _.get(response, AGENT_VOIP_CONFIGURATION_ADAPTER_TYPE_FIELD) === ADAPTER_TYPE_WIRELESS
    ) {
        _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_NO_REGISTRATION_MESSAGE, voipWirelessNotAvailable);
        _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_REGISTRATION_STATUS, VOIP_REGISTRATION_STATUS_ERROR);
        _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_HAS_REGISTRATION_STATUS, false);

        return;
    }

    // We have tried to register at one point, now we need to see if there is current registration info to show
    //  If the registration info is 'Pending' or the nPoint is not running then we really have nothing to show
    //  populate a message and let the user know.
    const hasRegistrationStatus =
        registrationStatus !== VOIP_REGISTRATION_STATUS_PENDING && agentStatus === AGENT_STATUS_RUNNING;
    _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_HAS_REGISTRATION_STATUS, hasRegistrationStatus);

    // The nPoint is not in a state where we can show registration information
    if (!hasRegistrationStatus) {
        const notRegisteredStatus = agentStatus === AGENT_STATUS_RUNNING ? registrationStatus : agentStatus;

        _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_REGISTRATION_STATUS, notRegisteredStatus);

        // We don't have a good status, let them know why we don't have registration information
        _.set(
            response,
            AGENT_VOIP_REGISTRATION_DETAIL_NO_REGISTRATION_MESSAGE,
            i18n._(
                t`Registration information is not available as the nPoint's status is currently ${notRegisteredStatus}`
            )
        );

        return;
    }

    // We have some sort of registration information to show.
    _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_REGISTRATION_STATUS, registrationStatus);
    _.set(
        response,
        AGENT_VOIP_REGISTRATION_DETAIL_LAST_REGISTERED,
        dateTimeFormatter(_.get(response, AGENT_VOIP_CONFIGURATION_REGISTRATION_UPDATED_AT_FIELD))
    );

    const isRegistered =
        registrationStatus === VOIP_REGISTRATION_STATUS_REGISTERED ||
        registrationStatus === VOIP_REGISTRATION_STATUS_DIRECT_CALLING;
    _.set(response, AGENT_VOIP_REGISTRATION_DETAIL_IS_REGISTERED, isRegistered);

    if (!_.isEmpty(_.get(response, AGENT_VOIP_CONFIGURATION_REGISTRATION_ERROR)) && isRegistered === false) {
        _.set(
            response,
            AGENT_VOIP_REGISTRATION_DETAIL_REGISTRATION_ERROR_OBJECT,
            cleanupSIPFlow(_.get(response, AGENT_VOIP_CONFIGURATION_REGISTRATION_SIGNALING_COLLECTION_FIELD))
        );
    }
}

function cleanupSIPFlow(sipFlowArray) {
    if (!sipFlowArray) {
        return [];
    }

    return _.map(sipFlowArray, (flow) => _.omit(flow, ['direction', 'call_id', 'CSeq']));
}
